/**
 * GET /v1/schools/:schoolId/messageable-moderators
 *
 * The authenticated caller's CONTEXTUAL moderator id set at the active school:
 * the ids of the effective moderators of the sections + groups the caller
 * belongs to. Session-derived (no client-supplied user id), schoolId-scoped,
 * rate-limited. Feeds the mobile new-message recipient picker's `moderator`
 * category (plan 2026-07-16-003, U3 → U4).
 *
 * Response is the id set ONLY — no names, roles, or grant metadata
 * (min-necessary-data). The overlay only ADDS visibility to people already in
 * the picker's `getSchoolUsers` candidate list, so no PII beyond ids is needed.
 *
 * ## Id-space (LOAD-BEARING)
 *
 * `moderatorSchoolUserIds` holds `UsersOnSchools.publicId` CUIDs — EXACTLY the
 * field the mobile picker keys `candidate.id` on. The `getSchoolUsers` endpoint
 * (`GET /v1/schools/:schoolId/(school-)users`) maps `id: user.publicId` in its
 * use-case (`get-all/use-case.ts`), so `candidate.id` over the wire is the
 * publicId — NOT the internal `UsersOnSchools.id` row id. U3 resolves U2's
 * internal-id set to these publicIds so `moderatorSet.has(candidate.id)` matches;
 * emitting internal ids instead would silently match NOBODY (the "admits nobody"
 * inert failure). Strings also sidestep JSON's inability to serialize bigint.
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-07-16-003-feat-chat-moderator-recipient-messageability-plan.md (U3)
 */

import { z } from 'zod'

/**
 * Messageable-moderators response — the caller's contextual moderator id set.
 * `moderatorSchoolUserIds` are `UsersOnSchools.publicId` CUIDs (the same id the
 * `getSchoolUsers` picker candidates carry as `id`). Deduped; excludes the
 * caller's own id.
 */
export const MessageableModeratorsResponseSchema = z.object({
  moderatorSchoolUserIds: z.array(z.string())
})
export type MessageableModeratorsResponse = z.infer<typeof MessageableModeratorsResponseSchema>
